import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail, Phone } from "lucide-react";

const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-conditions-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Terms and Conditions
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">
              Last updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>

            <div className="prose prose-slate max-w-none">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing and using the Ecommerce Brokers website (ecommercebrokers.com.au) 
                and services, you agree to be bound by these Terms and Conditions. If you do 
                not agree with any part of these terms, you must not use our website or services.
              </p>
              <p>
                Ecommerce Brokers is operated by Bonza Business & Franchise Sales ("we", "us", 
                or "our"), an Australian business brokerage service.
              </p>

              <h2>2. Services Description</h2>
              <p>Ecommerce Brokers provides:</p>
              <ul>
                <li>Online business valuation guides and tools for ecommerce businesses</li>
                <li>Connection to experienced business brokers</li>
                <li>Information and resources about selling ecommerce businesses</li>
                <li>Broker appointment booking services</li>
              </ul>

              <h2>3. Valuation Tool Disclaimer</h2>
              <p>
                The online valuation tool provides an <strong>indicative guide only</strong> 
                based on the information you provide. It is not a formal business appraisal 
                or valuation. The results should not be relied upon for making financial 
                decisions without seeking professional advice.
              </p>
              <p>
                For a detailed understanding of valuation limitations, please refer to our 
                <Link to="/disclaimer" className="text-blue-600 hover:underline"> Valuation Disclaimer</Link>.
              </p>

              <h2>4. User Responsibilities</h2>
              <p>When using our services, you agree to:</p>
              <ul>
                <li>Provide accurate and truthful information</li>
                <li>Not misrepresent your business or financial information</li>
                <li>Not use the service for any unlawful purpose</li>
                <li>Not attempt to gain unauthorised access to our systems</li>
                <li>Not interfere with the proper functioning of our website</li>
                <li>Maintain the confidentiality of any account credentials</li>
              </ul>

              <h2>5. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and 
                software, is the property of Ecommerce Brokers or Bonza Business & Franchise 
                Sales and is protected by Australian and international copyright laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, or create derivative works from 
                any content without our prior written consent.
              </p>

              <h2>6. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by Australian law, Ecommerce Brokers and 
                Bonza Business & Franchise Sales:
              </p>
              <ul>
                <li>
                  Make no warranties or representations about the accuracy or completeness 
                  of the website content or valuation results
                </li>
                <li>
                  Are not liable for any direct, indirect, incidental, consequential, or 
                  punitive damages arising from your use of our services
                </li>
                <li>
                  Are not responsible for any business decisions made based on valuation 
                  results or information provided
                </li>
                <li>
                  Do not guarantee any particular outcome from business sale processes
                </li>
              </ul>

              <h2>7. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Ecommerce Brokers, Bonza Business 
                & Franchise Sales, and their officers, directors, employees, and agents from 
                any claims, damages, losses, or expenses arising from your use of our services 
                or violation of these terms.
              </p>

              <h2>8. Third-Party Services</h2>
              <p>
                Our website may contain links to third-party websites or services. We are not 
                responsible for the content, accuracy, or practices of these external sites. 
                Your use of third-party services is at your own risk.
              </p>

              <h2>9. Broker Services</h2>
              <p>
                When you engage with a business broker through our service:
              </p>
              <ul>
                <li>
                  Any broker engagement is subject to separate terms and conditions between 
                  you and the broker
                </li>
                <li>
                  Brokers operate under their own professional licenses and insurance
                </li>
                <li>
                  Commission structures and fees are determined by the individual broker 
                  and disclosed before engagement
                </li>
                <li>
                  We facilitate introductions but do not guarantee broker performance or 
                  sale outcomes
                </li>
              </ul>

              <h2>10. Privacy</h2>
              <p>
                Your use of our services is also governed by our 
                <Link to="/privacy" className="text-blue-600 hover:underline"> Privacy Policy</Link>, 
                which explains how we collect, use, and protect your personal information.
              </p>

              <h2>11. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms and Conditions at any time. 
                Changes will be effective immediately upon posting to the website. Your 
                continued use of our services after changes constitutes acceptance of the 
                modified terms.
              </p>

              <h2>12. Termination</h2>
              <p>
                We may terminate or suspend your access to our services at any time, without 
                prior notice, for conduct that we believe violates these terms or is harmful 
                to other users or our business interests.
              </p>

              <h2>13. Governing Law</h2>
              <p>
                These Terms and Conditions are governed by the laws of Australia. Any disputes 
                arising from these terms or your use of our services will be subject to the 
                exclusive jurisdiction of the courts of Australia.
              </p>

              <h2>14. Severability</h2>
              <p>
                If any provision of these terms is found to be unenforceable or invalid, 
                that provision will be limited or eliminated to the minimum extent necessary, 
                and the remaining provisions will remain in full force and effect.
              </p>

              <h2>15. Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 not-prose">
                <p className="font-semibold text-slate-900 mb-2">Bonza Business & Franchise Sales</p>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:sales@bonzabfs.com.au" className="text-blue-600 hover:underline">
                      sales@bonzabfs.com.au
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href="tel:1300266922" className="text-blue-600 hover:underline">
                      1300 266 922
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-8 brightness-0 invert" />
              <span className="text-sm">Powered by Bonza Business & Franchise Sales</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-white">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
